"use strict";
import { TradeType, Token, CurrencyAmount } from "@uniswap/sdk-core";
import { useMemo } from "react";
import { useAccountMeta, useUniswapContextSelector } from "contexts/UniswapContext";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { FeatureFlags } from "features/gating/flags";
import { useFeatureFlag } from "features/gating/hooks";
import { useOnChainCurrencyBalance } from "features/portfolio/api";
import { ValueType, getCurrencyAmount } from "features/tokens/getCurrencyAmount";
import { NativeCurrency } from "features/tokens/NativeCurrency";
import { useCurrencyInfo } from "features/tokens/useCurrencyInfo";
import { useUSDCValue } from "features/transactions/hooks/useUSDCPrice";
import { useTransactionSettingsContext } from "features/transactions/settings/contexts/TransactionSettingsContext";
import { useTrade } from "features/transactions/swap/hooks/useTrade";
import { getWrapType, isWrapAction } from "features/transactions/swap/utils/wrap";
import { CurrencyField } from "types/currency";
import { buildCurrencyId } from "utils/uniswapCurrencyId";
export function useDerivedSwapInfo({
  isDebouncing,
  ...state
}) {
  const {
    [CurrencyField.INPUT]: currencyAssetIn,
    [CurrencyField.OUTPUT]: currencyAssetOut,
    exactAmountFiat,
    exactAmountToken,
    exactCurrencyField,
    focusOnCurrencyField = CurrencyField.INPUT,
    selectingCurrencyField,
    txId
  } = state;
  const { customSlippageTolerance, customDeadline, selectedProtocols, isV4HookPoolsEnabled } = useTransactionSettingsContext();
  const account = useAccountMeta();
  const { defaultChainId } = useEnabledChains();
  const currencyInInfo = useCurrencyInfo(
    currencyAssetIn ? buildCurrencyId(currencyAssetIn.chainId, currencyAssetIn.address) : void 0,
    { refetch: true }
  );
  const currencyOutInfo = useCurrencyInfo(
    currencyAssetOut ? buildCurrencyId(currencyAssetOut.chainId, currencyAssetOut.address) : void 0,
    { refetch: true }
  );
  const currencies = useMemo(() => {
    return {
      [CurrencyField.INPUT]: currencyInInfo,
      [CurrencyField.OUTPUT]: currencyOutInfo
    };
  }, [currencyInInfo, currencyOutInfo]);
  const currencyIn = currencyInInfo?.currency;
  const currencyOut = currencyOutInfo?.currency;
  const chainId = currencyIn?.chainId ?? currencyOut?.chainId ?? defaultChainId;
  const { balance: tokenInBalance } = useOnChainCurrencyBalance(currencyIn, account?.address);
  const { balance: tokenOutBalance } = useOnChainCurrencyBalance(currencyOut, account?.address);
  const wrapType = getWrapType(currencyIn, currencyOut);
  const isWrap = isWrapAction(wrapType);
  const isExactIn = isWrap ? true : exactCurrencyField === CurrencyField.INPUT;
  const otherCurrency = isExactIn ? currencyOut : currencyIn;
  const exactCurrency = isExactIn ? currencyIn : currencyOut;
  const properExactCurrency = useMemo(() => {
    if (!exactCurrency) return null;
    if (exactCurrency.isNative) {
      return NativeCurrency.onChain(exactCurrency.chainId);
    }
    if (exactCurrency.isToken && exactCurrency.address) {
      return new Token(
        exactCurrency.chainId,
        exactCurrency.address,
        exactCurrency.decimals,
        exactCurrency.symbol,
        exactCurrency.name
      );
    }
    return exactCurrency;
  }, [exactCurrency]);
  const amountSpecified = useMemo(() => {
    return getCurrencyAmount({
      value: exactAmountToken,
      valueType: ValueType.Exact,
      currency: properExactCurrency
    });
  }, [exactAmountToken, properExactCurrency]);
  const otherAmountForWrap = useMemo(() => {
    if (isWrap) {
      return getCurrencyAmount({
        value: exactAmountToken,
        valueType: ValueType.Exact,
        currency: otherCurrency
      });
    }
    return void 0;
  }, [exactAmountToken, isWrap, otherCurrency]);
  const sendPortionEnabled = useFeatureFlag(FeatureFlags.PortionFields);
  const getGeneratePermitAsTransaction = useUniswapContextSelector((ctx) => ctx.getGeneratePermitAsTransaction);
  const tradeParams = {
    account,
    amountSpecified: isWrap ? null : amountSpecified,
    otherCurrency,
    tradeType: isExactIn ? TradeType.EXACT_INPUT : TradeType.EXACT_OUTPUT,
    customSlippageTolerance,
    customDeadline,
    selectedProtocols,
    sendPortionEnabled,
    isDebouncing,
    getGeneratePermitAsTransaction,
    isV4HookPoolsEnabled
  };
  const trade = useTrade(tradeParams);
  const displayableTrade = trade.trade ?? trade.indicativeTrade;
  const currencyAmounts = useMemo(
    () => isWrap ? {
      [CurrencyField.INPUT]: amountSpecified,
      [CurrencyField.OUTPUT]: otherAmountForWrap
    } : {
      [CurrencyField.INPUT]: exactCurrencyField === CurrencyField.INPUT ? amountSpecified : restoreNativeCurrency(displayableTrade?.inputAmount, currencyIn),
      [CurrencyField.OUTPUT]: exactCurrencyField === CurrencyField.OUTPUT ? amountSpecified : restoreNativeCurrency(displayableTrade?.outputAmount, currencyOut)
    },
    [
      isWrap,
      exactCurrencyField,
      amountSpecified,
      otherAmountForWrap,
      displayableTrade?.inputAmount,
      displayableTrade?.outputAmount,
      currencyIn,
      currencyOut
    ]
  );
  const inputCurrencyUSDValue = useUSDCValue(currencyAmounts[CurrencyField.INPUT]);
  const outputCurrencyUSDValue = useUSDCValue(currencyAmounts[CurrencyField.OUTPUT]);
  const currencyAmountsUSDValue = useMemo(() => {
    return {
      [CurrencyField.INPUT]: inputCurrencyUSDValue,
      [CurrencyField.OUTPUT]: outputCurrencyUSDValue
    };
  }, [inputCurrencyUSDValue, outputCurrencyUSDValue]);
  const currencyBalances = useMemo(() => {
    return {
      [CurrencyField.INPUT]: tokenInBalance,
      [CurrencyField.OUTPUT]: tokenOutBalance
    };
  }, [tokenInBalance, tokenOutBalance]);
  return useMemo(() => {
    return {
      chainId,
      currencies,
      currencyAmounts,
      currencyAmountsUSDValue,
      currencyBalances,
      trade,
      exactAmountToken,
      exactAmountFiat,
      exactCurrencyField,
      focusOnCurrencyField,
      wrapType,
      selectingCurrencyField,
      txId
    };
  }, [
    chainId,
    currencies,
    currencyAmounts,
    currencyAmountsUSDValue,
    currencyBalances,
    exactAmountFiat,
    exactAmountToken,
    exactCurrencyField,
    focusOnCurrencyField,
    selectingCurrencyField,
    trade,
    txId,
    wrapType
  ]);
}
function restoreNativeCurrency(tradeAmount, userCurrency) {
  if (!tradeAmount || !userCurrency) {
    return tradeAmount;
  }
  if (userCurrency.isNative && !tradeAmount.currency.isNative) {
    return CurrencyAmount.fromRawAmount(userCurrency, tradeAmount.quotient);
  }
  return tradeAmount;
}
